import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { handleError } from '../utils/utils';

import {
    CreatePacksDrawer,
    PacksDrawerRecord,
    UpdatePacksDrawer,
} from '../types/packsDrawerTypes';

type BasepathOption = {
    basepath?: string;
};

const DEFAULT_BASEPATH = '/api';
export const PACKS_DRAWER_QUERY_KEY = 'packs-drawer';
const EMPTY_RECORDS: PacksDrawerRecord[] = [];

const getBasepath = (basepath?: string) => basepath ?? DEFAULT_BASEPATH;
const getPacksDrawerUrl = (basepath: string) => `${basepath}/packs-drawer`;
const getPacksDrawerItemUrl = (basepath: string, id: string) =>
    `${basepath}/packs-drawer/${id}`;
const getPacksDrawerQueryKey = (basepath: string) =>
    [PACKS_DRAWER_QUERY_KEY, basepath] as const;
const getPacksDrawerItemQueryKey = (basepath: string, id: string) =>
    [PACKS_DRAWER_QUERY_KEY, basepath, id] as const;

export function usePacksDrawerList(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const query = useQuery({
        queryKey: getPacksDrawerQueryKey(basepath),
        queryFn: async () => {
            const { data } = await axios.get<PacksDrawerRecord[]>(
                getPacksDrawerUrl(basepath),
            );
            return data;
        },
    });

    return {
        records: query.data ?? EMPTY_RECORDS,
        isLoading: query.isLoading,
        errorMessage: query.error
            ? handleError('Fetch packs drawer error', query.error, false)
            : undefined,
        fetch: query.refetch,
    };
}

export function usePacksDrawerRecord(id: string, options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const query = useQuery({
        queryKey: getPacksDrawerItemQueryKey(basepath, id),
        queryFn: async () => {
            const { data } = await axios.get<PacksDrawerRecord>(
                getPacksDrawerItemUrl(basepath, id),
            );
            return data;
        },
        enabled: Boolean(id),
    });

    return {
        record: query.data ?? null,
        isLoading: query.isLoading,
        errorMessage: query.error
            ? handleError('Fetch packs drawer record error', query.error, false)
            : undefined,
        fetch: query.refetch,
    };
}

export function useCreatePacksDrawer(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (body: CreatePacksDrawer) => {
            const { data } = await axios.post<number>(
                getPacksDrawerUrl(basepath),
                body,
            );
            return data;
        },
        onSettled: () => {
            client.invalidateQueries({
                queryKey: getPacksDrawerQueryKey(basepath),
            });
        },
    });

    return {
        recordId: mutation.data ?? null,
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError(
                  'Create packs drawer record error',
                  mutation.error,
                  true,
              )
            : undefined,
        createRecord: mutation.mutateAsync,
    };
}

export function useUpdatePacksDrawer(id: string, options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (body: UpdatePacksDrawer) => {
            const { data } = await axios.put<PacksDrawerRecord>(
                getPacksDrawerItemUrl(basepath, id),
                body,
            );
            return data;
        },
        onSuccess: (updated) => {
            client.setQueryData(
                getPacksDrawerItemQueryKey(basepath, id),
                updated,
            );
        },
        onSettled: () => {
            client.invalidateQueries({
                queryKey: getPacksDrawerQueryKey(basepath),
            });
        },
    });

    return {
        updatedRecord: mutation.data ?? null,
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError(
                  'Update packs drawer record error',
                  mutation.error,
                  true,
              )
            : undefined,
        updateRecord: mutation.mutateAsync,
    };
}

export function useUpdatePacksDrawerInEnv(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async ({
            id,
            body,
        }: {
            id: number;
            body: UpdatePacksDrawer;
        }) => {
            const { data } = await axios.put<PacksDrawerRecord>(
                getPacksDrawerItemUrl(basepath, String(id)),
                body,
            );
            return data;
        },
        onSettled: () => {
            client.invalidateQueries({
                queryKey: getPacksDrawerQueryKey(basepath),
            });
        },
    });

    return {
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError(
                  'Update packs drawer record error',
                  mutation.error,
                  true,
              )
            : undefined,
        updateRecord: (id: number, body: UpdatePacksDrawer) =>
            mutation.mutateAsync({ id, body }),
    };
}

export function useUpdatePacksDrawerPriorities(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (ids: number[]) => {
            await axios.put(`${getPacksDrawerUrl(basepath)}/priorities`, {
                ids,
            });
        },
        onSettled: () => {
            client.invalidateQueries({
                queryKey: getPacksDrawerQueryKey(basepath),
            });
        },
    });

    return {
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError('Reorder packs drawer error', mutation.error, true)
            : undefined,
        updatePriorities: mutation.mutateAsync,
    };
}

export function useDeletePacksDrawer(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (id: PacksDrawerRecord['id']) => {
            await axios.delete(getPacksDrawerItemUrl(basepath, String(id)));
        },
        onSuccess: (_data, id) => {
            client.removeQueries({
                queryKey: getPacksDrawerItemQueryKey(basepath, String(id)),
            });
        },
        onSettled: () => {
            client.invalidateQueries({
                queryKey: getPacksDrawerQueryKey(basepath),
            });
        },
    });

    return {
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError(
                  'Delete packs drawer record error',
                  mutation.error,
                  true,
              )
            : undefined,
        deleteRecord: mutation.mutateAsync,
    };
}
